'use client';

import { useEffect, useMemo } from 'react';
import { isNil } from 'es-toolkit';
import { graphql } from 'src/gql';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { TaxOverviewTable } from './tax-overview/tax-overview-table';
import { useTaxOverviewParams } from './tax-overview/tax-overview-url-state';

export const TaxEnrollmentsQuery = graphql(/* GraphQL */ `
  query TaxEnrollmentTableRowsEmbedded(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $sortField: TaxEnrollmentSortValues
    $sortDirection: SortDirection
    $requireAdvisorActionFilter: Boolean
    $requireClientActionFilter: Boolean
    $waitingOnThirdPartyFilter: Boolean
    $query: String
    $enrollmentTypeFilter: [TaxEnrollmentTypeName!]
    $statusFilter: [TaxEnrollmentStatus!]
    $taxYearFilter: [Int!]
  ) {
    ...TaxOverviewTable
  }
`);

export function TaxContent() {
  const { pagination, params } = useTaxOverviewParams();

  const variables = useMemo(() => {
    const taxYearFilterInts: number[] | null = !isNil(params.taxYearFilter)
      ? params.taxYearFilter.map((year): number => Number.parseInt(year, 10))
      : null;

    return {
      first: pagination.first,
      after: pagination.after,
      last: pagination.last,
      before: pagination.before,
      sortField: params.sortBy,
      sortDirection: params.sortByOrder,
      requireAdvisorActionFilter: params.requireAdvisorActionFilter,
      requireClientActionFilter: params.requireClientActionFilter,
      waitingOnThirdPartyFilter: params.waitingOnThirdPartyFilter,
      query: params.searchQuery ?? null,
      enrollmentTypeFilter: params.enrollmentTypeFilter ?? null,
      statusFilter: params.statusFilter ?? null,
      taxYearFilter: taxYearFilterInts,
    };
  }, [pagination, params]);

  const { data, loading, refetch } = useQueryWithErrorLogging(TaxEnrollmentsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Listen for cross-tab updates via localStorage StorageEvent
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'taxEnrollmentVersion' && !isNil(e.newValue) && e.oldValue !== e.newValue) {
        void refetch();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refetch]);

  return <TaxOverviewTable queryFrag={data} loading={loading} />;
}
